import {ExternalLinkIcon} from '@heroicons/react/outline';
import React, {useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';
import {
  isBusinessOwner,
  requestableBusinessUserKYCDocuments,
} from '@afriex/core/utils';

import {Badge, Button, Link, PermissionsProvider} from '@/components';
import {useCountryList} from '@/context/CountryListContext';
import KYCImage from '@/screens/KYC/Details/KYCImage';
import {InfoRow} from '@/screens/Merchants/Kyc/common';
import {getVerificationBadge} from '@/screens/Merchants/Kyc/verificationBadge';
import {BusinessKYCDocumentType} from '@/types/BusinessKYCDocumentType';
import {statusColors} from '@/types/Transaction';
import {mapCountryCodeToFlags} from '@/utils/countries';
import {logRejection} from '@/utils/logRejection';

import RequestAdditionalDocumentsModal from '../RequestAdditionalDocumentsModal';
import {getKYCIdentity} from './kycIdentity';
import KYCModal from './KYCModal';
import type {KYCSummaryProps} from './types';

const KYCSummary: React.FC<KYCSummaryProps> = ({
  user,
  extraRow,
  onSubmit,
  onRequestAdditionalDocuments,
  isKycSubmitting,
  userId,
  userSelfie,
  onFlagUpdate,
  userLoadingFlags,
  idType,
}) => {
  const {state: countryListState} = useCountryList();
  const {countryList} = countryListState;
  const country = user?.countryCode?.toUpperCase() || '';
  const {
    isSecurityFlagged = false,
    isVerified = false,
    isDeactivated = false,
  } = user?.meta?.flags ?? {};
  const isOwner = isBusinessOwner(user);

  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [openRequestDocumentsModal, setOpenRequestDocumentsModal] =
    useState(false);
  const selfieDoc = (user?.meta?.kyc?.docs ?? [])?.find(
    (doc) => doc?.idType === BusinessKYCDocumentType.Selfie,
  );

  const docSelfie = selfieDoc?.media?.source ?? '';
  const {
    idType: identityIdType,
    idNumber,
    idExpiration,
    dateOfBirth,
  } = getKYCIdentity(user);
  const [modalType, setModalType] = useState<'Profile' | 'Documents'>(
    'Profile',
  );

  return (
    <div className="mt-5">
      <div className="flex flex-col md:flex-row bg-white dark:bg-transparent rounded-lg w-full px-5 py-5 gap-5 border border-gray-200 dark:border-dark-borderMid">
        <div className="w-full self-center md:w-1/4">
          <KYCImage
            src={userSelfie || docSelfie}
            alt="User selfie"
            name={user?.name?.fullName}
          />
        </div>
        <div className="bg-white dark:bg-transparent rounded-lg w-full">
          <div className="flex flex-col md:flex-row gap-3 justify-between mb-3">
            <div className="flex md:items-center gap-4">
              <h2 className="font-semibold text-md md:text-lg self-center">
                {user.name?.fullName}
              </h2>
              {getVerificationBadge(isVerified || false)}
            </div>

            <div className="flex md:justify-end gap-3 my-3 md:my-0 md:items-center">
              <PermissionsProvider
                permission={AfriexAdminPermission.BusinessKYCUpdate}
              >
                <Button
                  variant="outline"
                  data-cy="merchant-kyc-update-info"
                  onClick={() => {
                    setModalType('Profile');
                    setOpenUploadModal(!openUploadModal);
                  }}
                >
                  Update Info
                </Button>

                <Button
                  variant="solid"
                  data-cy="merchant-kyc-upload-documents"
                  onClick={() => {
                    setModalType('Documents');
                    setOpenUploadModal(!openUploadModal);
                  }}
                >
                  Upload Documents
                </Button>
              </PermissionsProvider>

              {/* EDD requests only make sense for the business owner (UBO):
                  the endpoint records the request on the owner's row and
                  emails them, so a request on a team member would be refused
                  (USER_NOT_FOUND). Hide the action for anyone else. */}
              {isOwner && (
                <PermissionsProvider
                  permission={
                    AfriexAdminPermission.BusinessDocumentRequestCreate
                  }
                >
                  <Button
                    variant="outline"
                    onClick={() => setOpenRequestDocumentsModal(true)}
                  >
                    Request Additional Documents
                  </Button>
                </PermissionsProvider>
              )}
            </div>
          </div>

          <div>
            {/* Country */}
            <InfoRow
              label="Country"
              value={
                country + ' ' + mapCountryCodeToFlags(countryList)[country]
              }
            />

            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
              <p className="text-slate-500 text-sm  dark:text-slate-100">
                User Details
              </p>
              <p className="text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
                <Link
                  to={`/merchants/users/${user?.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    variant="ghost"
                    className="no-padding no-margin hover:underline"
                    rightIcon={<ExternalLinkIcon className="w-4 h-4" />}
                  >
                    View User Details
                  </Button>
                </Link>
              </p>
            </div>

            {/* Date of Birth */}
            <InfoRow
              label="Date of Birth"
              value={dateOfBirth || '-'}
              dataCy="merchant-kyc-date-of-birth"
            />

            {/* ID Type */}
            <InfoRow
              label="ID Type"
              value={identityIdType || '-'}
              dataCy="merchant-kyc-id-type"
            />

            {/* UserID */}
            <InfoRow
              label="ID Number"
              value={idNumber || '-'}
              dataCy="merchant-kyc-id-number"
            />
            <InfoRow
              label="ID Expiry Date"
              value={idExpiration || '-'}
              dataCy="merchant-kyc-id-expiry"
            />
            <InfoRow label="User ID" value={user?.id} />

            {/* Address */}
            <InfoRow
              label="Address"
              value={user?.meta?.kyc?.data?.ADDRESS}
              dataCy="merchant-kyc-address"
            />

            {/* Representative Type */}
            <InfoRow
              label="Representative Type"
              value={
                user?.meta?.kyc?.data?.REPRESENTATIVE_TYPE || user?.role || ''
              }
              dataCy="merchant-kyc-representative-type"
            />

            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900 rounded">
              <p className="text-slate-500 text-sm dark:text-slate-100">
                Verified
              </p>
              <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100 break-words flex gap-3">
                <Badge
                  dataCy="merchant-kyc-verified-badge"
                  label={isVerified ? 'Yes' : 'No'}
                  colorScheme={
                    statusColors[isVerified ? 'successful' : 'failed']
                  }
                  className="px-4 !rounded-3xl"
                />
                <PermissionsProvider
                  permission={AfriexAdminPermission.BusinessUserFlagUpdate}
                >
                  <Button
                    data-cy="merchant-kyc-verified-toggle"
                    disabled={userLoadingFlags['isVerified']}
                    variant="unStyled"
                    onClick={() =>
                      onFlagUpdate({
                        isVerified: !isVerified,
                      })
                    }
                    className="hover:underline text-cyan-700"
                  >
                    {isVerified ? 'Unverify' : 'Verify'}
                  </Button>
                </PermissionsProvider>
              </p>
            </div>

            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900 rounded">
              <p className="text-slate-500 text-sm dark:text-slate-100">
                Security Flagged
              </p>
              <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100 break-words flex gap-3">
                <Badge
                  dataCy="merchant-kyc-security-flagged-badge"
                  label={isSecurityFlagged ? 'Yes' : 'No'}
                  colorScheme={
                    statusColors[isSecurityFlagged ? 'failed' : 'successful']
                  }
                  className="px-4 !rounded-3xl"
                />
                <PermissionsProvider
                  permission={AfriexAdminPermission.BusinessUserFlagUpdate}
                >
                  <Button
                    data-cy="merchant-kyc-security-flagged-toggle"
                    disabled={userLoadingFlags['isSecurityFlagged']}
                    variant="unStyled"
                    onClick={() =>
                      onFlagUpdate({
                        isSecurityFlagged: !isSecurityFlagged,
                      })
                    }
                    className="hover:underline text-cyan-700"
                  >
                    {isSecurityFlagged ? 'Unblock' : 'Block'}
                  </Button>
                </PermissionsProvider>
              </p>
            </div>

            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900 rounded">
              <p className="text-slate-500 text-sm dark:text-slate-100">
                Deactivated
              </p>
              <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100 break-words flex gap-3">
                <Badge
                  dataCy="merchant-kyc-deactivated-badge"
                  label={isDeactivated ? 'Yes' : 'No'}
                  colorScheme={
                    statusColors[isDeactivated ? 'failed' : 'successful']
                  }
                  className="px-4 !rounded-3xl"
                />
                <PermissionsProvider
                  permission={AfriexAdminPermission.BusinessUserFlagUpdate}
                >
                  <Button
                    data-cy="merchant-kyc-deactivated-toggle"
                    disabled={userLoadingFlags['isDeactivated']}
                    variant="unStyled"
                    onClick={() => {
                      // `onFlagUpdate` (useMerchantKYC.updateUserFlag) toasts
                      // its own failures, so this only fires if that handler
                      // itself throws. Log rather than swallow silently.
                      onFlagUpdate({
                        isDeactivated: !isDeactivated,
                      }).catch(
                        logRejection('Failed to toggle user activation flag'),
                      );
                    }}
                    className="hover:underline text-cyan-700"
                  >
                    {isDeactivated ? 'Activate' : 'Deactivate'}
                  </Button>
                </PermissionsProvider>
              </p>
            </div>
          </div>

          {extraRow || ''}
        </div>
      </div>
      <KYCModal
        isOpen={openUploadModal}
        onClose={() => setOpenUploadModal(false)}
        type={modalType}
        onSubmit={onSubmit}
        isKycSubmitting={isKycSubmitting}
        userId={userId}
        user={user}
        idType={idType}
      />

      {isOwner && (
        <RequestAdditionalDocumentsModal
          isOpen={openRequestDocumentsModal}
          onClose={() => setOpenRequestDocumentsModal(false)}
          options={requestableBusinessUserKYCDocuments}
          onSubmit={onRequestAdditionalDocuments}
          description="Select the documents you require from this business owner from the list below"
        />
      )}
    </div>
  );
};

export default KYCSummary;
